import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import { createFragmentContainer, graphql } from 'react-relay';
import classnames from 'classnames';

// Styles
import styles from './SharedItemTile.scss';

// Components
import Tile from 'dibs-search-product-tile';
import { FavoritesItem } from '../../components/global/FavoriteIcons/FavoritesItem';
import { CurrencyProvider } from 'dibs-buyer-layout/exports/CurrencyProvider';

// Helpers
import get from 'lodash.get';

const getDimensionLabel = (measurement, selectedUnit) => {
    let label;
    const size = measurement.size || {};
    const dimensionValues = (measurement.dimensions || [])
        .filter(({ unit }) => (unit ? unit === selectedUnit : true))
        .map(dimension => dimension.value);

    if (size.value) {
        label = (
            <FormattedMessage
                id="sb.SharedItemTile.sizeLabel"
                defaultMessage="Size: {size}"
                values={{
                    size: size.value,
                }}
            />
        );
    } else {
        label = dimensionValues[0];
    }

    return label;
};

export function SharedItemTileComponent(props) {
    const {
        children,
        favorites,
        index,
        item,
        isClient,
        isMobile,
        lazyLoadStartIndex,
        onClick,
        onContentLoaded,
        showInternalSellerName,
        showSellerName,
        showCreatorName,
        showMeasurements,
        measurementUnit,
        quickViewProps,
        imageLoadVerticalOffset,
        onContentVisible,
        customTileWidthClass,
        imageSize,
    } = props;
    const { activeQuickView, closeQuickView, openQuickView } = quickViewProps || {};
    const measurement = get(item, 'measurement') || {};
    const itemId = get(item, 'serviceId') || '';
    const showQuickView = !!openQuickView && !!closeQuickView;

    return (
        <CurrencyProvider
            render={({ currency, showCurrency }) => (
                <div
                    className={classnames(
                        styles.tileWrapper,
                        { [styles.tileWidth]: !customTileWidthClass },
                        { [customTileWidthClass]: !!customTileWidthClass }
                    )}
                    data-tn="item-tile-wrapper"
                >
                    {favorites ? (
                        <FavoritesItem
                            wrapperClass={styles.favoritesWrapper}
                            heartIconWrapperClass={styles.heartIconWrapper}
                            folderIconWrapperClass={styles.folderIconWrapper}
                            itemId={itemId}
                            item={item}
                            fetchFolder={false}
                            {...favorites}
                        />
                    ) : null}
                    <Tile
                        index={index}
                        item={item}
                        currency={currency}
                        showPrice={showCurrency}
                        itemsPerRow={lazyLoadStartIndex}
                        showInternalSellerName={showInternalSellerName}
                        showByLine={!isMobile}
                        showSellerName={showSellerName}
                        showCreatorName={showCreatorName}
                        onClick={onClick}
                        onContentLoaded={onContentLoaded}
                        onContentVisible={onContentVisible}
                        activeQuickView={activeQuickView}
                        openQuickView={openQuickView}
                        closeQuickView={closeQuickView}
                        dimensions={
                            showMeasurements ? getDimensionLabel(measurement, measurementUnit) : ''
                        }
                        isMobile={isMobile}
                        showQuickView={showQuickView}
                        showQuickViewIcon={!!isClient}
                        imageLoadVerticalOffset={imageLoadVerticalOffset}
                        useLoFiLazyLoader
                        srcSetSizes="(max-width:768px) 50vw, (max-width:1024px) 33vw, 25vw"
                        imageSize={imageSize}
                    />
                    {typeof children === 'function'
                        ? children({ tileHoveredClass: styles.tileHovered })
                        : children}
                </div>
            )}
        />
    );
}

SharedItemTileComponent.defaultProps = {
    isMobile: true,
    lazyLoadStartIndex: 2,
    size: 'medium',
};

SharedItemTileComponent.propTypes = {
    children: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
    favorites: PropTypes.object,
    quickViewProps: PropTypes.shape({
        activeQuickView: PropTypes.number,
        closeQuickView: PropTypes.func,
        openQuickView: PropTypes.func,
    }),
    index: PropTypes.number.isRequired,
    isMobile: PropTypes.bool,
    item: PropTypes.object.isRequired,
    lazyLoadStartIndex: PropTypes.number,
    imageLoadVerticalOffset: PropTypes.number,
    onClick: PropTypes.func,
    onContentLoaded: PropTypes.func,
    showInternalSellerName: PropTypes.bool,
    showSellerName: PropTypes.bool,
    showCreatorName: PropTypes.bool,
    showMeasurements: PropTypes.bool,
    measurementUnit: PropTypes.string,
    isClient: PropTypes.bool.isRequired,
    onContentVisible: PropTypes.func,
    customTileWidthClass: PropTypes.string,
    imageSize: PropTypes.string,
};

const mapStateToProps = state => {
    return {
        isClient: get(state, 'relayVariables.variables.isClient'),
    };
};

export const SharedItemTile = createFragmentContainer(
    connect(mapStateToProps)(SharedItemTileComponent),
    {
        item: graphql`
            fragment SharedItemTile_item on Item {
                ...Tile_item @arguments(showSeller: $showSeller, isTrade: $isTrade)
                serviceId
                measurement {
                    dimensions: display(
                        addLabel: true
                        separator: " "
                        fields: [height, width, depth, diameter]
                    ) {
                        unit
                        value
                    }
                    size {
                        value
                    }
                }
                browseUrl
                categoryCode
            }
        `,
    }
);
